import React, { useCallback, useId, useRef, useState } from 'react';
import {
  getScratchCoverPreset,
  isPlaceholderTextureUrl,
} from './scratchCoverPresets';
import {
  SCRATCH_BRUSH_SIZE,
  appendTrailPoint,
  scratchCellAt,
  scratchPercent,
  scratchPointAt,
  scratchThreshold,
  type ScratchStroke,
} from './scratchReveal';

/**
 * WEB-926, позиция 1 — скретч-карта скидки (референс BetterMe: тикет с
 * перфорацией «Scratch it off» перед пейволлом). Юзер стирает защитный слой и
 * «выигрывает» скидку вместо того, чтобы пассивно её получить.
 *
 * ДОСТУПНОСТЬ (решение атома). Стирание — не единственный путь, иначе воронка
 * встаёт у всех, кто не может или не понял, что надо тереть:
 *  1) текст скидки лежит в обычном DOM ПОД слоем — скринридер читает его сразу,
 *     а слой помечен aria-hidden как чистое оформление;
 *  2) под картой всегда есть настоящая <button> «Показать скидку» —
 *     полноценный таб-стоп, срабатывает по Enter/Space без всякого драга;
 *  3) поверхность стирания — не интерактивный элемент для ассистивных
 *     технологий (кнопка уже покрывает эту роль), поэтому она не плодит второй
 *     невнятный таб-стоп «canvas».
 */

interface ScratchCardBlockProps {
  coverLabel?: string;
  /** WEB-957 этап 2: пресет текстуры покрытия (реестр scratchCoverPresets). */
  coverPreset?: string;
  /** WEB-957 этап 2: своя картинка покрытия — сильнее пресета. */
  coverImageUrl?: string;
  revealTitle?: string;
  revealNote?: string;
  coverColor?: string;
  thresholdPercent?: number;
  /** Текст кнопки-обхода. Задаётся вызывающим ради единого словаря строк. */
  revealButtonLabel: string;
}

interface Point {
  clientX: number;
  clientY: number;
}

/** Координаты указателя/пальца из любого поддерживаемого события. */
function pointOf(
  event: React.PointerEvent | React.MouseEvent | React.TouchEvent,
): Point | null {
  const touches = (event as React.TouchEvent).touches;
  if (touches && touches.length > 0) {
    return { clientX: touches[0].clientX, clientY: touches[0].clientY };
  }
  const mouse = event as React.MouseEvent;
  if (typeof mouse.clientX === 'number' && typeof mouse.clientY === 'number') {
    return { clientX: mouse.clientX, clientY: mouse.clientY };
  }
  return null;
}

export function ScratchCardBlock({
  coverLabel,
  coverPreset,
  coverImageUrl,
  revealTitle,
  revealNote,
  coverColor,
  thresholdPercent,
  revealButtonLabel,
}: ScratchCardBlockProps): React.ReactElement {
  const [erased, setErased] = useState<ReadonlySet<number>>(() => new Set<number>());
  // WEB-957: след стирания — штрихи в пикселях поверхности; рисуется
  // SVG-маской (мягкая круглая кисть), сетка ячеек осталась только счётчиком.
  const [strokes, setStrokes] = useState<ReadonlyArray<ScratchStroke>>([]);
  const [surfaceSize, setSurfaceSize] = useState({ width: 320, height: 160 });
  const [revealed, setRevealed] = useState(false);
  const pressedRef = useRef(false);
  const surfaceRef = useRef<HTMLDivElement | null>(null);
  const maskUid = useId().replace(/[^a-zA-Z0-9_-]/g, '');

  const threshold = scratchThreshold(thresholdPercent);
  const percent = revealed ? 100 : scratchPercent(erased);

  const eraseAt = useCallback(
    (point: Point | null) => {
      const surface = surfaceRef.current;
      if (!surface || !point) return;
      const rect = surface.getBoundingClientRect();
      const trailPoint = scratchPointAt(rect, point.clientX, point.clientY);
      if (trailPoint) {
        setSurfaceSize((prev) =>
          prev.width === rect.width && prev.height === rect.height
            ? prev
            : { width: rect.width, height: rect.height },
        );
        setStrokes((prev) => appendTrailPoint(prev, trailPoint));
      }
      const cell = scratchCellAt(rect, point.clientX, point.clientY);
      if (cell == null) return;
      setErased((prev) => {
        if (prev.has(cell)) return prev;
        const next = new Set(prev);
        next.add(cell);
        if (scratchPercent(next) >= threshold) setRevealed(true);
        return next;
      });
    },
    [threshold],
  );

  // Поддержаны и pointer-, и mouse/touch-события: набор стёртых ячеек —
  // множество, повторное попадание в ту же ячейку идемпотентно, поэтому
  // дублирующая доставка событий браузером ничего не ломает, зато механика
  // работает и там, где PointerEvent недоступен.
  const handleDown = (event: React.PointerEvent | React.MouseEvent | React.TouchEvent) => {
    pressedRef.current = true;
    // Каждое нажатие — СВОЙ штрих: иначе отрыв пальца соединялся бы перемычкой.
    setStrokes((prev) => [...prev, []]);
    eraseAt(pointOf(event));
  };
  const handleMove = (event: React.PointerEvent | React.MouseEvent) => {
    const pressed = pressedRef.current || event.buttons === 1;
    if (!pressed) return;
    eraseAt(pointOf(event));
  };
  const handleTouchMove = (event: React.TouchEvent) => {
    eraseAt(pointOf(event));
  };
  const handleUp = () => {
    pressedRef.current = false;
  };

  // WEB-957: приоритет покрытия — своя картинка → текстура пресета →
  // сплошной цвет. Плейсхолдерный URL битой картинкой не рендерим —
  // падаем в цвет-фолбэк пресета.
  const preset = getScratchCoverPreset(coverPreset);
  const coverTextureUrl =
    coverImageUrl && !isPlaceholderTextureUrl(coverImageUrl)
      ? coverImageUrl
      : preset && !isPlaceholderTextureUrl(preset.url)
        ? preset.url
        : null;
  const cover =
    coverColor && !preset
      ? coverColor
      : (preset?.fallbackColor ??
        coverColor ??
        // on-surface не нужен: это цвет ЗАЛИВКИ стираемого слоя, не текста.
        'var(--accent-money, var(--color-text-primary, #1f1000))');
  // Надпись гаснет по мере стирания — подсказка нужна только пока слой цел.
  const labelOpacity = revealed ? 0 : Math.max(0, 1 - percent / threshold);

  return (
    <div
      data-testid="scratch-card"
      data-revealed={revealed ? 'true' : 'false'}
      data-percent={percent}
      style={{ marginBottom: 16 }}
    >
      <div
        style={{
          position: 'relative',
          overflow: 'hidden',
          borderRadius: 'var(--radius-card, var(--border-radius-lg, 12px))',
          minHeight: 140,
          background: 'var(--color-bg-surface, #fafafa)',
          border: '1px dashed var(--accent-money, var(--color-border-primary, #d9d9d9))',
        }}
      >
        <div
          data-testid="scratch-card-prize"
          style={{
            padding: '32px 24px',
            textAlign: 'center',
          }}
        >
          {revealTitle && (
            <div
              style={{
                fontSize: 32,
                fontWeight: 700,
                lineHeight: 1.1,
                // Живая приёмка WEB-1440: заголовок выигрыша наследовал цвет
                // экрана и пропадал на светлой заливке карточки.
                color: 'var(--color-text-on-surface, var(--color-text-primary, inherit))',
              }}
            >
              {revealTitle}
            </div>
          )}
          {revealNote && (
            <div
              style={{
                marginTop: 8,
                fontSize: 14,
                // WEB-1440 (класс WEB-966): подпись выигрыша лежит на карточке.
                color: 'var(--color-text-on-surface, var(--color-text-secondary, #595959))',
              }}
            >
              {revealNote}
            </div>
          )}
        </div>

        <div
          ref={surfaceRef}
          data-testid="scratch-card-surface"
          aria-hidden="true"
          onPointerDown={handleDown}
          onPointerMove={handleMove}
          onPointerUp={handleUp}
          onPointerLeave={handleUp}
          onMouseDown={handleDown}
          onMouseMove={handleMove}
          onMouseUp={handleUp}
          onTouchStart={handleDown}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleUp}
          style={{
            position: 'absolute',
            inset: 0,
            // Раскрыто — слой перестаёт ловить события и перехватывать текст.
            pointerEvents: revealed ? 'none' : 'auto',
            opacity: revealed ? 0 : 1,
            transition: 'opacity var(--transition-ms, 300ms) ease',
            cursor: revealed ? 'default' : 'grab',
            // Без этого мобильный браузер уводит палец в скролл страницы.
            touchAction: 'none',
            userSelect: 'none',
          }}
        >
          <div
            data-testid="scratch-card-cover"
            aria-hidden="true"
            style={{ position: 'absolute', inset: 0 }}
          >
          {/* WEB-957: покрытие — один слой под SVG-маской. След указателя —
              непрерывные линии с круглыми концами; мягкие края даёт
              feGaussianBlur на чёрном следе маски. Сетка плиток убрана из
              вида и живёт только счётчиком площади (scratchCellAt). */}
          <svg
            viewBox={`0 0 ${surfaceSize.width} ${surfaceSize.height}`}
            preserveAspectRatio="none"
            aria-hidden="true"
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', display: 'block' }}
          >
            <defs>
              <filter
                id={`scratch-soft-${maskUid}`}
                x="-20%"
                y="-20%"
                width="140%"
                height="140%"
              >
                <feGaussianBlur stdDeviation="4" />
              </filter>
              <mask id={`scratch-mask-${maskUid}`} maskUnits="userSpaceOnUse">
                <rect
                  x="0"
                  y="0"
                  width={surfaceSize.width}
                  height={surfaceSize.height}
                  fill="#ffffff"
                />
                <g
                  data-testid="scratch-card-trail"
                  filter={`url(#scratch-soft-${maskUid})`}
                >
                  {strokes.map((stroke, i) =>
                    stroke.length === 0 ? null : stroke.length === 1 ? (
                      <circle
                        key={i}
                        cx={stroke[0].x}
                        cy={stroke[0].y}
                        r={SCRATCH_BRUSH_SIZE / 2}
                        fill="#000000"
                      />
                    ) : (
                      <polyline
                        key={i}
                        points={stroke.map((pt) => `${pt.x},${pt.y}`).join(' ')}
                        fill="none"
                        stroke="#000000"
                        strokeWidth={SCRATCH_BRUSH_SIZE}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    ),
                  )}
                </g>
              </mask>
            </defs>
            {/* WEB-957 хвост: цвет-постер лежит ПОД текстурой всегда. Он же
                страховка: пока картинка грузится (или не загрузилась вовсе —
                хранилище недоступно, автор снёс свой файл), слой остаётся
                непрозрачным и приз не видно без стирания. */}
            <rect
              x="0"
              y="0"
              width={surfaceSize.width}
              height={surfaceSize.height}
              fill={cover}
              mask={`url(#scratch-mask-${maskUid})`}
            />
            {coverTextureUrl && (
              <image
                href={coverTextureUrl}
                x="0"
                y="0"
                width={surfaceSize.width}
                height={surfaceSize.height}
                preserveAspectRatio="xMidYMid slice"
                mask={`url(#scratch-mask-${maskUid})`}
              />
            )}
          </svg>
          {coverLabel && (
            <div
              style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '0 24px',
                textAlign: 'center',
                fontSize: 16,
                fontWeight: 600,
                color: 'var(--color-bg-primary, #ffffff)',
                opacity: labelOpacity,
                transition: 'opacity 120ms linear',
                pointerEvents: 'none',
              }}
            >
              {coverLabel}
            </div>
          )}
          </div>
        </div>
      </div>

      {!revealed && (
        <div style={{ textAlign: 'center', marginTop: 8 }}>
          <button
            type="button"
            data-testid="scratch-card-reveal-button"
            onClick={() => setRevealed(true)}
            style={{
              background: 'none',
              border: 'none',
              padding: 8,
              cursor: 'pointer',
              fontSize: 14,
              textDecoration: 'underline',
              // on-surface не нужен: кнопка стоит ПОД карточкой, на фоне экрана.
              color: 'var(--color-text-secondary, #595959)',
            }}
          >
            {revealButtonLabel}
          </button>
        </div>
      )}
    </div>
  );
}
